import React, { useState, useEffect, useRef, useCallback } from "react";
import * as api from "../services/projectApi";

// Project-scoped AI chat (spec §12). Answers are grounded in this project's
// data and read-only — the assistant only suggests, never acts.
export default function ChatPanel({ projectId }) {
  const [conversations, setConversations] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const threadRef = useRef(null);

  const loadConversations = useCallback(() => {
    api.listConversations(projectId).then(setConversations).catch(() => {});
  }, [projectId]);

  useEffect(() => { loadConversations(); }, [loadConversations]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages, sending]);

  const openConversation = async (id) => {
    setConversationId(id);
    setError(null);
    try {
      setMessages(await api.getConversationMessages(projectId, id));
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    }
  };

  const newChat = () => {
    setConversationId(null);
    setMessages([]);
    setError(null);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setError(null);
    setMessages((m) => [...m, { role: "user", content: text, _local: true }]);
    setSending(true);
    try {
      const res = await api.sendChat(projectId, { conversationId, message: text });
      setConversationId(res.conversationId);
      setMessages((m) => [...m, { role: "assistant", content: res.reply }]);
      if (!conversationId) loadConversations();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      setMessages((m) => m.filter((x) => !x._local)); // roll back the optimistic user bubble
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const STARTERS = [
    "How is my project performing?",
    "Are we on track to reach our goal?",
    "Which platform is performing better?",
    "What should I investigate next?",
  ];

  return (
    <div className="chat">
      <div className="chat-head">
        <h4 style={{ margin: 0 }}>Project AI Chat</h4>
        <div className="chat-head-actions">
          {conversations.length > 0 && (
            <select
              className="chat-select"
              value={conversationId || ""}
              onChange={(e) => (e.target.value ? openConversation(e.target.value) : newChat())}
            >
              <option value="">New conversation…</option>
              {conversations.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.title || "Untitled"}
                </option>
              ))}
            </select>
          )}
          <button className="proj-btn" onClick={newChat}>New chat</button>
        </div>
      </div>

      <p className="chat-hint proj-muted">
        Ask about this project's performance. Answers are grounded in its data and read-only — no ad changes are made.
      </p>

      <div className="chat-thread" ref={threadRef}>
        {messages.length === 0 && !sending && (
          <div className="chat-starters">
            {STARTERS.map((s) => (
              <button key={s} className="chat-starter" onClick={() => setInput(s)}>{s}</button>
            ))}
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`chat-msg ${m.role}`}>
            <div className="chat-bubble">{m.content}</div>
          </div>
        ))}
        {sending && (
          <div className="chat-msg assistant">
            <div className="chat-bubble chat-typing">Thinking…</div>
          </div>
        )}
      </div>

      {error && <div className="proj-alert">{error}</div>}

      <div className="chat-input">
        <textarea
          rows={2}
          value={input}
          placeholder="Ask about this project's performance…"
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          disabled={sending}
        />
        <button className="proj-btn proj-btn-primary" onClick={send} disabled={sending || !input.trim()}>
          {sending ? "…" : "Send"}
        </button>
      </div>
    </div>
  );
}
